import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const TestCreatorView = ({ questions, addTest, onCancel }) => {
  const [name, setName] = useState('');
  const [timeLimitPerQuestion, setTimeLimitPerQuestion] = useState(30);
  const [passingScore, setPassingScore] = useState(70);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddQuestion = (id) => {
    if (!selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => [...prev, id]);
    }
  };

  const handleRemoveQuestion = (id) => {
    setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
  };

  const moveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === selectedQuestionIds.length - 1)) {
      return;
    }
    const newIds = [...selectedQuestionIds];
    const idToMove = newIds[index];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    newIds[index] = newIds[swapIndex];
    newIds[swapIndex] = idToMove;
    setSelectedQuestionIds(newIds);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Test name cannot be empty.');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      setError('You must select at least one question for the test.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await addTest({
        name,
        questionIds: selectedQuestionIds,
        timeLimitPerQuestion: Number(timeLimitPerQuestion) || 0,
        passingScore: Number(passingScore) > 0 ? Number(passingScore) : undefined
      });
    } catch (err) {
      setError('Failed to create test. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedQuestions = selectedQuestionIds.map(id => questions.find(q => q.id === id)).filter(q => !!q);
  const availableQuestions = questions.filter(q => !selectedQuestionIds.includes(q.id));

  return (
    <div className="bg-slate-800 p-6 rounded-lg shadow-xl">
      <h2 className="text-2xl font-bold mb-6 text-indigo-400">Create New Test</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label htmlFor="test-name" className="block text-sm font-medium text-slate-300 mb-2">Test Name</label>
            <input
              type="text"
              id="test-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., React Hooks Chapter 1"
            />
          </div>
          <div>
            <label htmlFor="time-limit" className="block text-sm font-medium text-slate-300 mb-2">Time Limit per Question (sec)</label>
            <input
              type="number"
              id="time-limit"
              value={timeLimitPerQuestion}
              onChange={(e) => setTimeLimitPerQuestion(Number(e.target.value))}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="0 for no limit"
              min="0"
            />
          </div>
          <div>
            <label htmlFor="passing-score" className="block text-sm font-medium text-slate-300 mb-2">Passing Score (%)</label>
            <input
              type="number"
              id="passing-score"
              value={passingScore}
              onChange={(e) => setPassingScore(Number(e.target.value))}
              className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., 70"
              min="1"
              max="100"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-slate-300 mb-3">Available Questions</h3>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto border border-slate-700 rounded-md p-2 bg-slate-900/50">
              {availableQuestions.length === 0 ? (
                <p className="text-slate-400 text-center p-4">All questions have been selected.</p>
              ) : (
                availableQuestions.map(q => (
                  <div key={q.id} className="flex items-center justify-between p-2 rounded-md bg-slate-700 hover:bg-slate-600 transition">
                    <div className="text-slate-200 text-sm flex-1 pr-2 prose prose-sm prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>{q.text}</ReactMarkdown>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => handleAddQuestion(q.id)} 
                      className="text-indigo-400 hover:text-indigo-300 text-sm font-semibold"
                    >
                      Add &rarr;
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-slate-300 mb-3">Test Questions ({selectedQuestionIds.length} selected)</h3>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto border border-slate-700 rounded-md p-2 bg-slate-900/50">
              {selectedQuestions.length === 0 ? (
                <p className="text-slate-400 text-center p-4">Add questions from the left.</p>
              ) : (
                selectedQuestions.map((q, index) => (
                  <div key={q.id} className="flex items-center justify-between p-2 rounded-md bg-slate-700">
                    <div className="text-slate-200 text-sm flex-1 pr-2 prose prose-sm prose-invert max-w-none flex items-center">
                      <span className="font-bold mr-2 not-prose">{index + 1}.</span> 
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: React.Fragment }}>{q.text}</ReactMarkdown>
                    </div>
                    <div className="flex items-center">
                      <button 
                        type="button" 
                        onClick={() => moveQuestion(index, 'up')} 
                        disabled={index === 0} 
                        className="p-1 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        ▲
                      </button>
                      <button 
                        type="button" 
                        onClick={() => moveQuestion(index, 'down')} 
                        disabled={index === selectedQuestions.length - 1} 
                        className="p-1 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        ▼
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleRemoveQuestion(q.id)} 
                        className="ml-2 text-red-400 hover:text-red-300 text-lg leading-none transition"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}
        
        <div className="flex items-center space-x-4">
          <button 
            type="button" 
            onClick={onCancel} 
            className="w-full bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-md transition"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isSubmitting} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition-transform transform hover:scale-105 shadow-lg disabled:bg-slate-600 disabled:cursor-wait"
          >
            {isSubmitting ? 'Creating...' : 'Create Test'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TestCreatorView;